import { inject, Injectable, Injector, signal, TemplateRef } from '@angular/core';

import { I18nService } from '@core/i18n-legacy';
import {
  TellentAssistantService,
  TellentAssistantThreadsService,
} from '@core/tellent-assistant-logic';
import type { ThreadSummary } from '@core/tellent-assistant-types';
import { ThreadMenuItemId, threadMenuGroups } from '@core/tellent-assistant-ui';
import {
  injectTellentMobileSheetPresenter,
  TellentMobileOptionsSheetComponent,
  TellentMobileSheetOption,
  TellentMobileSheetOptionIconContext,
  TellentMobileSheetPresenter,
} from '@core/tellent-mobile-ui';
import { DialogService, IconName } from '@core/user-interface-legacy';

/**
 * Touch replacement for the thread drawer's hover kebab menu: presents the
 * shared thread menu (threadMenuGroups) as a bottom sheet via the
 * host-provided mobile sheet presenter, and applies the chosen action.
 * Chat-panel-provided (not root) so dialogs inherit the panel's OVERLAY_LEVEL.
 */
@Injectable()
export class ThreadOptionsSheetService {
  private readonly i18n = inject(I18nService);
  private readonly threadsService = inject(TellentAssistantThreadsService);
  private readonly assistantService = inject(TellentAssistantService);
  private readonly dialogService = inject(DialogService);
  private readonly injector = inject(Injector);
  private readonly presenter: TellentMobileSheetPresenter | null =
    injectTellentMobileSheetPresenter({ optional: true });

  private readonly thread = signal<ThreadSummary | null>(null);

  public iconName(option: TellentMobileSheetOption): IconName {
    const thread = this.thread();
    const item = thread
      ? threadMenuGroups(thread)
          .flat()
          .find(menuItem => menuItem.id === option.id)
      : undefined;
    return item?.icon ?? 'pin';
  }

  public apply(id: ThreadMenuItemId, thread: ThreadSummary): void {
    if (id === 'pin') {
      this.threadsService.setThreadPinned(thread.id, !thread.pinned);
    } else if (id === 'remove') {
      this.confirmRemove(thread);
    }
  }

  public async open(
    threadId: string,
    iconTemplate: TemplateRef<TellentMobileSheetOptionIconContext>,
  ): Promise<void> {
    const thread = this.threadsService.threads().find(t => t.id === threadId);
    if (!thread || !this.presenter) return;
    this.thread.set(thread);
    const groups = threadMenuGroups(thread);

    const handle = await this.presenter.createSheet({
      component: TellentMobileOptionsSheetComponent,
      background: 'neutral',
      canDismiss: true,
      componentProps: {
        title: thread.title,
        dialogOnlyTitle: true,
        optionGroups: groups.map(group =>
          group.map(item => ({
            id: item.id,
            label: this.i18n.translate(item.labelKey),
            destructive: item.destructive,
          })),
        ),
        iconTemplate,
        selectHandler: (option: TellentMobileSheetOption) => void handle.dismiss(option),
        dismissHandler: () => void handle.dismiss(),
      },
    });
    await handle.present();

    const { data } = await handle.onWillDismiss<TellentMobileSheetOption>();
    const selected = groups.flat().find(item => item.id === data?.id);
    if (selected) {
      this.apply(selected.id, thread);
    }
  }

  private confirmRemove(thread: ThreadSummary): void {
    const title =
      thread.title.trim() || this.i18n.translate('tlc.tellent_assistant.thread.default_title');
    this.dialogService
      .error({
        title: this.i18n.translate('tlc.tellent_assistant.remove_dialog.title'),
        message: this.i18n.translate('tlc.tellent_assistant.remove_dialog.message', { title }),
        agreeLabel: this.i18n.translate('tlc.tellent_assistant.remove_dialog.confirm'),
        injector: this.injector,
      })
      .subscribe(confirmed => {
        if (!confirmed) return;
        this.threadsService.deleteThread(thread.id);
        if (thread.id === this.assistantService.currentThreadId()) {
          this.assistantService.newThread();
        }
      });
  }
}
